import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Pencil, ChefHat, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { money } from "../../lib/format";
import PageHeader from "../../components/PageHeader";
import { useAuth } from "../../context/AuthContext";
import { usePos } from "../../context/PosContext";

const EMPTY = {
  name: "",
  code: "",
  sku: "",
  barcode: "",
  description: "",
  category_id: "",
  base_price: 0,
  stock_qty: 0,
  image_url: "",
  variant_group_ids: [],
  modifier_group_ids: [],
  active: true,
  kitchen_id: "",
};

export default function ProductsPage() {
  const { hasPerm, hasFeature } = useAuth();
  const { outlets, outletId } = usePos();

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vgroups, setVgroups] = useState([]);
  const [mgroups, setMgroups] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [routes, setRoutes] = useState([]);

  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [loadingKitchens, setLoadingKitchens] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const canManageKitchen = hasPerm("kitchen.manage");
  const canManageCatalog = hasPerm("catalog.manage");
  const kitchenFeatureEnabled = hasFeature("KITCHEN");
  const canAssignKitchen = canManageKitchen && kitchenFeatureEnabled;

  const routeMap = useMemo(() => {
    const map = {};

    routes.forEach((route) => {
      if (route.product_id) {
        map[route.product_id] = route.kitchen_id || "";
      }
    });

    return map;
  }, [routes]);

  const loadProducts = useCallback(() => {
    api
      .get("/products", { params: q ? { q } : {} })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [q]);

  const loadCatalogOptions = useCallback(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/variant-groups"),
      api.get("/modifier-groups"),
    ])
      .then(([categoriesRes, variantsRes, modifiersRes]) => {
        setCategories(categoriesRes.data);
        setVgroups(variantsRes.data);
        setMgroups(modifiersRes.data);
      })
      .catch(() => {});
  }, []);

  const loadKitchenRouting = useCallback(async () => {
    if (!outletId || !kitchenFeatureEnabled) {
      setKitchens([]);
      setRoutes([]);
      return;
    }

    setLoadingKitchens(true);

    try {
      const [kitchensRes, routesRes] = await Promise.all([
        api.get("/kitchens", {
          params: { outlet_id: outletId },
        }),
        api.get("/product-kitchen-routes", {
          params: { outlet_id: outletId },
        }),
      ]);

      setKitchens(
        Array.isArray(kitchensRes.data)
          ? kitchensRes.data
          : kitchensRes.data?.items || []
      );

      setRoutes(
        Array.isArray(routesRes.data)
          ? routesRes.data
          : routesRes.data?.items || []
      );
    } catch (err) {
      toast.error(apiError(err, "Gagal memuat kitchen routing."));
      setKitchens([]);
      setRoutes([]);
    } finally {
      setLoadingKitchens(false);
    }
  }, [outletId, kitchenFeatureEnabled]);

  useEffect(() => {
    loadProducts();
    loadCatalogOptions();
  }, [loadProducts, loadCatalogOptions]);

  useEffect(() => {
    loadKitchenRouting();
  }, [loadKitchenRouting]);

  useEffect(() => {
    const t = setTimeout(loadProducts, 250);
    return () => clearTimeout(t);
  }, [loadProducts]);

  useEffect(() => {
    setEditing(null);
  }, [outletId]);

  const filteredItems = useMemo(() => {
    if (!categoryFilter) return items;

    return items.filter((product) => product.category_id === categoryFilter);
  }, [items, categoryFilter]);

  const uploadProductImage = async (file) => {
    if (!editing?.id || isNew) {
      toast.info("Simpan product terlebih dahulu, lalu upload gambar melalui Edit.");
      return;
    }

    if (!canManageCatalog) {
      toast.error("Anda tidak memiliki permission catalog.manage.");
      return;
    }

    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Format gambar harus JPG, JPEG, PNG, atau WEBP.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Ukuran gambar maksimal 2 MB.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploadingImage(true);

    try {
      const response = await api.post(
        `/products/${editing.id}/image`,
        formData
      );

      const data =
        response.data?.image ||
        response.data?.product ||
        response.data ||
        {};

      setEditing((current) => ({
        ...current,
        image_url: data.image_url || "",
        image_asset_id: data.image_asset_id || "",
        image_content_type: data.image_content_type || "",
        image_size: data.image_size || 0,
      }));

      await loadProducts();

      toast.success("Gambar product berhasil di-upload.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingImage(false);
    }
  };

  const removeProductImage = async () => {
    if (!editing?.id || isNew) return;

    if (!canManageCatalog) {
      toast.error("Anda tidak memiliki permission catalog.manage.");
      return;
    }

    const confirmed = window.confirm("Hapus gambar product ini?");

    if (!confirmed) return;

    setUploadingImage(true);

    try {
      await api.delete(`/products/${editing.id}/image`);

      setEditing((current) => ({
        ...current,
        image_url: "",
        image_asset_id: "",
        image_content_type: "",
        image_size: 0,
      }));

      await loadProducts();

      toast.success("Gambar product berhasil dihapus.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingImage(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...editing,
        base_price: parseInt(editing.base_price, 10) || 0,
      };

      delete payload.kitchen_id;
      delete payload.image_url;
      delete payload.image_asset_id;
      delete payload.image_content_type;
      delete payload.image_size;

      let savedProductId = editing.id;

      if (isNew) {
        const response = await api.post("/products", payload);
        savedProductId =
          response.data?.id ||
          response.data?.product?.id ||
          response.data?._id;
      } else {
        await api.patch(`/products/${editing.id}`, payload);
      }

      if (!isNew && canAssignKitchen && outletId && savedProductId && editing.kitchen_id) {
        await api.put("/product-kitchen-routes", {
          outlet_id: outletId,
          product_id: savedProductId,
          kitchen_id: editing.kitchen_id,
        });
      }

      toast.success(isNew ? "Product created" : "Product updated");
      setEditing(null);
      loadProducts();

      if (!isNew) {
        loadKitchenRouting();
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openEdit = (product) => {
    setEditing({
      ...EMPTY,
      ...product,
      kitchen_id: routeMap[product.id] || "",
      variant_group_ids: Array.isArray(product.variant_group_ids)
        ? product.variant_group_ids
        : [],
      modifier_group_ids: Array.isArray(product.modifier_group_ids)
        ? product.modifier_group_ids
        : [],
    });

    setIsNew(false);
  };

  const openNew = () => {
    setEditing({
      ...EMPTY,
      category_id: categories[0]?.id || "",
    });

    setIsNew(true);
  };

  const toggleList = (key, id) => {
    setEditing((s) => ({
      ...s,
      [key]: s[key].includes(id)
        ? s[key].filter((x) => x !== id)
        : [...s[key], id],
    }));
  };

  const catName = (id) =>
    categories.find((c) => c.id === id)?.name || "—";

  const activeKitchens = kitchens.filter(
    (kitchen) => kitchen.active !== false
  );

  return (
    <div data-testid="products-page">
      <PageHeader
        title="Products"
        subtitle="Your sellable catalog"
        testid="products-header"
      >
        <button
          onClick={openNew}
          data-testid="product-create-button"
          className="h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-bold flex items-center gap-2"
        >
          <Plus size={16} /> New Product
        </button>
      </PageHeader>

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />

          <input
            data-testid="products-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, code, SKU, barcode…"
            className="w-full h-11 pl-9 pr-3 rounded-xl bg-card border border-border text-sm focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => setCategoryFilter("")}
          className={`px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${
            categoryFilter === ""
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-card border-border hover:bg-secondary"
          }`}
        >
          All
        </button>

        {categories.map((category) => (
          <button
            type="button"
            key={category.id}
            onClick={() => setCategoryFilter(category.id)}
            className={`px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${
              categoryFilter === category.id
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card border-border hover:bg-secondary"
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3 hidden md:table-cell">Category</th>
              <th className="px-4 py-3 hidden lg:table-cell">
                SKU / Barcode
              </th>
              <th className="px-4 py-3 text-right">Base Price</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>

          <tbody>
            {filteredItems.map((p) => (
              <tr
                key={p.id}
                data-testid={`product-row-${p.code || p.id}`}
                className="border-b border-border/50 hover:bg-secondary/40"
              >
                <td
                  className="px-4 py-3 cursor-pointer"
                  onClick={() => openEdit(p)}
                >
                  <p className="font-semibold">{p.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {p.code}
                  </p>
                </td>

                <td
                  className="px-4 py-3 hidden md:table-cell cursor-pointer"
                  onClick={() => openEdit(p)}
                >
                  {catName(p.category_id)}
                </td>

                <td
                  className="px-4 py-3 hidden lg:table-cell font-mono text-xs text-muted-foreground cursor-pointer"
                  onClick={() => openEdit(p)}
                >
                  {p.sku} {p.barcode && `· ${p.barcode}`}
                </td>

                <td
                  className="px-4 py-3 text-right font-mono font-bold cursor-pointer"
                  onClick={() => openEdit(p)}
                >
                  {money(p.base_price)}
                </td>

                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded-md text-[11px] font-bold ${
                        p.active
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {p.active ? "ACTIVE" : "INACTIVE"}
                    </span>

                    <button
                      type="button"
                      onClick={() => openEdit(p)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 text-[11px] font-bold"
                      data-testid={`product-edit-${p.code || p.id}`}
                    >
                      <Pencil size={12} />
                      EDIT
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {filteredItems.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-10 text-center text-muted-foreground"
                >
                  No products.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
        >
          <form
            onSubmit={save}
            className="bg-card border border-border w-full max-w-lg rounded-2xl p-6 space-y-3 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="product-form-modal"
          >
            <h3 className="font-heading text-lg font-bold">
              {isNew ? "New Product" : `Edit ${editing.name}`}
            </h3>

            <input
              data-testid="product-form-name"
              value={editing.name}
              onChange={(e) =>
                setEditing({ ...editing, name: e.target.value })
              }
              placeholder="Product name"
              required
              className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
            />

            <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Product Stock
                </label>
                <input
                  data-testid="product-form-stock"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.stock_qty ?? 0}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      stock_qty: e.target.value,
                    })
                  }
                  placeholder="0"
                  className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
                />
                <p className="text-[11px] text-muted-foreground">
                  Stok produk jadi / finished goods.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2">
              <input
                data-testid="product-form-code"
                value={editing.code}
                onChange={(e) =>
                  setEditing({ ...editing, code: e.target.value })
                }
                placeholder="Code"
                className="h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
              />

              <input
                data-testid="product-form-sku"
                value={editing.sku}
                onChange={(e) =>
                  setEditing({ ...editing, sku: e.target.value })
                }
                placeholder="SKU"
                className="h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
              />

              <input
                data-testid="product-form-barcode"
                value={editing.barcode}
                onChange={(e) =>
                  setEditing({ ...editing, barcode: e.target.value })
                }
                placeholder="Barcode"
                className="h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div
                className="col-span-2 rounded-xl border border-border bg-secondary/30 p-3"
                data-testid="product-image-section"
              >
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon size={16} className="text-primary" />
                  <p className="text-xs font-semibold text-muted-foreground">
                    Product Image
                  </p>
                </div>

                <div className="flex gap-3 items-start">
                  <div className="w-24 h-24 rounded-xl border border-border bg-card overflow-hidden flex items-center justify-center shrink-0">
                    {editing.image_url ? (
                      <img
                        src={editing.image_url}
                        alt={editing.name || "Product"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon
                        size={28}
                        className="text-muted-foreground"
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    {isNew ? (
                      <p className="text-[11px] text-muted-foreground">
                        Simpan product terlebih dahulu. Setelah itu buka Edit
                        untuk upload gambar.
                      </p>
                    ) : canManageCatalog ? (
                      <>
                        <label
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card text-xs font-semibold cursor-pointer hover:bg-secondary ${
                            uploadingImage
                              ? "opacity-60 pointer-events-none"
                              : ""
                          }`}
                          data-testid="product-image-upload"
                        >
                          <Upload size={14} />
                          {uploadingImage
                            ? "Uploading..."
                            : editing.image_url
                              ? "Replace Image"
                              : "Upload Image"}

                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            disabled={uploadingImage}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              uploadProductImage(file);
                            }}
                          />
                        </label>

                        {editing.image_url && (
                          <button
                            type="button"
                            onClick={removeProductImage}
                            disabled={uploadingImage}
                            className="ml-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-xs font-semibold hover:bg-destructive/20 disabled:opacity-60"
                            data-testid="product-image-remove"
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                        )}

                        <p className="text-[11px] text-muted-foreground mt-2">
                          JPG, JPEG, PNG, WEBP · maksimal 2 MB
                        </p>

                        {editing.image_size > 0 && (
                          <p className="text-[11px] text-muted-foreground mt-1">
                            {(editing.image_size / 1024).toFixed(0)} KB
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">
                        Upload gambar membutuhkan permission catalog.manage.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <select
                data-testid="product-form-category"
                value={editing.category_id}
                onChange={(e) =>
                  setEditing({ ...editing, category_id: e.target.value })
                }
                className="h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <input
                data-testid="product-form-price"
                type="number"
                min="0"
                value={editing.base_price}
                onChange={(e) =>
                  setEditing({ ...editing, base_price: e.target.value })
                }
                placeholder="Base price"
                required
                className="h-11 px-3 rounded-lg bg-secondary border border-border text-sm focus:border-primary focus:outline-none font-mono"
              />
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">
                Variant groups
              </p>

              <div className="flex flex-wrap gap-2">
                {vgroups.map((g) => (
                  <button
                    type="button"
                    key={g.id}
                    onClick={() => toggleList("variant_group_ids", g.id)}
                    data-testid={`product-form-vg-${g.name.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                      editing.variant_group_ids.includes(g.id)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-secondary border-border"
                    }`}
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1.5">
                Modifier groups
              </p>

              <div className="flex flex-wrap gap-2">
                {mgroups.map((g) => (
                  <button
                    type="button"
                    key={g.id}
                    onClick={() => toggleList("modifier_group_ids", g.id)}
                    data-testid={`product-form-mg-${g.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                      editing.modifier_group_ids.includes(g.id)
                        ? "bg-blue-500 text-white border-blue-500"
                        : "bg-secondary border-border"
                    }`}
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            </div>

            {kitchenFeatureEnabled && (
              <div className="rounded-xl border border-border bg-secondary/40 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <ChefHat size={16} className="text-primary" />

                  <p className="text-xs font-semibold text-muted-foreground">
                    Assign Kitchen
                  </p>
                </div>

                <select
                  value={editing.kitchen_id || ""}
                  disabled={
                    isNew ||
                    !canAssignKitchen ||
                    loadingKitchens ||
                    activeKitchens.length === 0
                  }
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      kitchen_id: e.target.value,
                    })
                  }
                  className="w-full h-11 px-3 rounded-lg bg-card border border-border text-sm focus:border-primary focus:outline-none disabled:opacity-60"
                  data-testid="product-form-kitchen"
                >
                  <option value="">
                    {loadingKitchens
                      ? "Loading kitchens..."
                      : activeKitchens.length === 0
                        ? "No active kitchen"
                        : "Belum dirouting"}
                  </option>

                  {activeKitchens.map((kitchen) => (
                    <option key={kitchen.id} value={kitchen.id}>
                      {kitchen.name}
                    </option>
                  ))}
                </select>

                {isNew ? (
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Simpan product terlebih dahulu, lalu Assign Kitchen melalui
                    Edit.
                  </p>
                ) : !canAssignKitchen ? (
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Perubahan kitchen membutuhkan permission kitchen.manage.
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Kitchen mengikuti outlet aktif:{" "}
                    {outlets.find((o) => o.id === outletId)?.name || "—"}.
                  </p>
                )}
              </div>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.active}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    active: e.target.checked,
                  })
                }
                data-testid="product-form-active"
              />
              Active
            </label>

            <div className="flex gap-3">
              <button
                type="button"
                data-testid="product-form-cancel"
                className="flex-1 h-11 rounded-xl border border-border bg-background text-foreground font-bold text-sm"
                onClick={() => setEditing(null)}
              >
                CANCEL
              </button>

              <button
                type="submit"
                data-testid="product-form-submit"
                className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
              >
                {isNew ? "Create Product" : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
